import { useState, useEffect } from 'react';
import { getCRMPipeline, updateLead } from '../../lib/api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { DollarSign, Users, ArrowRight } from 'lucide-react';

const STAGE_META = {
  new_lead: { label: 'New Lead', color: 'border-zinc-700 bg-zinc-950' },
  contacted: { label: 'Contacted', color: 'border-blue-500/20 bg-blue-500/10' },
  demo_scheduled: { label: 'Demo Scheduled', color: 'border-indigo-500/20 bg-indigo-500/10' },
  demo_completed: { label: 'Demo Completed', color: 'border-purple-500/20 bg-purple-500/10' },
  follow_up: { label: 'Follow Up', color: 'border-yellow-500/20 bg-yellow-500/10' },
  proposal: { label: 'Proposal', color: 'border-orange-500/20 bg-orange-500/10' },
  contract: { label: 'Contract', color: 'border-teal-500/20 bg-teal-500/10' },
  closed_won: { label: 'Closed Won', color: 'border-emerald-500/20 bg-emerald-500/10' },
  closed_lost: { label: 'Closed Lost', color: 'border-red-500/20 bg-red-500/10' },
};

const STAGE_ORDER = Object.keys(STAGE_META);

function getNextStage(current) {
  const idx = STAGE_ORDER.indexOf(current);
  if (idx < 0 || idx >= STAGE_ORDER.length - 2) return null; // skip closed_lost
  return STAGE_ORDER[idx + 1];
}

export default function CRMPage() {
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      const res = await getCRMPipeline();
      setPipeline(res.data.pipeline);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPipeline(); }, []);

  const moveToStage = async (leadId, newStage) => {
    await updateLead(leadId, { crm_stage: newStage });
    fetchPipeline();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-900/50 border-t-blue-400 rounded-full animate-spin" />
      </div>
    );
  }

  const totalPipelineValue = pipeline.reduce((sum, s) => sum + s.total_value, 0);
  const totalLeads = pipeline.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-100">CRM Pipeline</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {totalLeads} leads &middot; ${totalPipelineValue.toLocaleString()} total pipeline
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 text-center">
          <p className="text-xs text-zinc-500 mb-1">Active Leads</p>
          <p className="text-xl font-semibold">{totalLeads}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-zinc-500 mb-1">Pipeline Value</p>
          <p className="text-xl font-semibold">${totalPipelineValue.toLocaleString()}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-emerald-400 mb-1">Won</p>
          <p className="text-xl font-semibold text-emerald-400">
            {pipeline.find((s) => s.stage === 'closed_won')?.count || 0}
          </p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-red-500 mb-1">Lost</p>
          <p className="text-xl font-semibold text-red-500">
            {pipeline.find((s) => s.stage === 'closed_lost')?.count || 0}
          </p>
        </Card>
      </div>

      {/* Pipeline Columns */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {pipeline.map((stage) => {
            const meta = STAGE_META[stage.stage] || STAGE_META.new_lead;
            return (
              <div key={stage.stage} className="w-72 flex-shrink-0">
                <div className={`rounded-xl border-2 ${meta.color} p-3`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-zinc-200">{meta.label}</h3>
                    <Badge variant="secondary" className="text-xs">{stage.count}</Badge>
                  </div>
                  {stage.total_value > 0 && (
                    <p className="text-xs text-zinc-500 mb-3 flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      ${stage.total_value.toLocaleString()}
                    </p>
                  )}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {stage.leads.length === 0 ? (
                      <p className="text-xs text-zinc-400 text-center py-4">No leads</p>
                    ) : (
                      stage.leads.map((lead) => {
                        const next = getNextStage(stage.stage);
                        return (
                          <div key={lead.id} className="bg-zinc-900 rounded-lg border border-zinc-800 p-3 shadow-sm">
                            <p className="text-sm font-medium text-zinc-100 truncate">{lead.email}</p>
                            {lead.company && <p className="text-xs text-zinc-500">{lead.company}</p>}
                            {lead.deal_value > 0 && (
                              <p className="text-xs text-emerald-400 mt-1">${lead.deal_value.toLocaleString()}</p>
                            )}
                            {next && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="mt-2 text-xs w-full justify-between text-blue-400 hover:bg-blue-500/10"
                                onClick={() => moveToStage(lead.id, next)}
                              >
                                Move to {STAGE_META[next]?.label}
                                <ArrowRight className="w-3 h-3" />
                              </Button>
                            )}
                            {stage.stage !== 'closed_won' && stage.stage !== 'closed_lost' && (
                              <div className="flex gap-1 mt-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs flex-1 text-emerald-400 hover:bg-emerald-500/10"
                                  onClick={() => moveToStage(lead.id, 'closed_won')}
                                >
                                  Won
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs flex-1 text-red-500 hover:bg-red-500/10"
                                  onClick={() => moveToStage(lead.id, 'closed_lost')}
                                >
                                  Lost
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
